import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  Home as HomeIcon,
  Megaphone,
  FileText,
  Calendar as CalendarIcon,
  Users,
  Briefcase,
  Info,
  Link as LinkIcon,
  Inbox,
  MessageCircle,
  Image as ImageIcon,
  ClipboardList,
  Receipt,
  ShieldCheck,
  History as HistoryIcon,
  LogOut,
} from "lucide-react";

const ADMIN_ACCESS_KEY = process.env.REACT_APP_ADMIN_KEY || "lakeunice2026";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, roles: ["owner", "admin", "editor"] },
  { to: "/admin/home", label: "Home Page", icon: HomeIcon, roles: ["owner", "admin", "editor"] },
  { to: "/admin/banner", label: "Announcements Banner", icon: Megaphone, roles: ["owner", "admin", "editor"] },
  { to: "/admin/postings", label: "Public Notices", icon: ClipboardList, roles: ["owner", "admin", "editor"] },
  { to: "/admin/minutes", label: "Meeting Minutes", icon: FileText, roles: ["owner", "admin", "editor"] },
  { to: "/admin/events", label: "Events Calendar", icon: CalendarIcon, roles: ["owner", "admin", "editor"] },
  { to: "/admin/officials", label: "Officials", icon: Users, roles: ["owner", "admin", "editor"] },
  { to: "/admin/projects", label: "Projects", icon: Briefcase, roles: ["owner", "admin", "editor"] },
  { to: "/admin/notices", label: "Information Notices", icon: Info, roles: ["owner", "admin", "editor"] },
  { to: "/admin/gallery", label: "Photo Gallery", icon: ImageIcon, roles: ["owner", "admin", "editor"] },
  { to: "/admin/links", label: "Links", icon: LinkIcon, roles: ["owner", "admin", "editor"] },
  { to: "/admin/chat", label: "Live Chat Inbox", icon: MessageCircle, roles: ["owner", "admin", "editor"] },
  { to: "/admin/contacts", label: "Feedback Inbox", icon: Inbox, roles: ["owner", "admin", "editor"] },
  { to: "/admin/billing", label: "Billing", icon: Receipt, roles: ["system", "owner", "admin"] },
  { to: "/admin/audit", label: "Audit Log", icon: HistoryIcon, roles: ["system", "owner", "admin"] },
  { to: "/admin/users", label: "Admin Accounts", icon: ShieldCheck, roles: ["system", "owner"] },
];

const AdminLayout = () => {
  const { isAuthed, loading, logout, email, role } = useAuth();
  const nav = useNavigate();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  }
  if (!isAuthed) return <Navigate to={`/admin/login?key=${ADMIN_ACCESS_KEY}`} replace />;

  const visibleNav = NAV.filter((n) => n.roles.includes(role));

  return (
    <div className="min-h-screen grid grid-cols-[16rem_1fr] bg-muted/30" data-testid="admin-layout">
      <aside className="bg-white border-r border-border flex flex-col">
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-[0.2em] text-secondary">Admin</div>
          <div className="font-heading text-lg font-semibold mt-0.5">Lake Eunice CMS</div>
          <div className="mt-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-primary/10 text-primary" data-testid="admin-role-badge">
            {role}
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" data-testid="admin-sidebar-nav">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted"
                }`
              }
            >
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="text-xs text-muted-foreground px-3 truncate">{email}</div>
          <button
            onClick={() => {
              logout();
              nav(`/admin/login?key=${ADMIN_ACCESS_KEY}`);
            }}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-foreground/80 hover:bg-muted"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
          <a
            href="https://frostbyteitmn.com"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-powered-by-frostbyte"
            className="mt-3 flex items-center gap-2 px-3 py-2 rounded-md hover:bg-muted/60 transition-colors"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_frosty-it-hub/artifacts/o2t5vwwy_logo.jpeg"
              alt="FrostByte IT"
              className="h-7 w-7 rounded-full object-cover ring-1 ring-border"
            />
            <div className="leading-tight">
              <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Powered by</div>
              <div className="text-xs font-semibold text-foreground/80">FrostByte IT</div>
            </div>
          </a>
        </div>
      </aside>
      <main className="overflow-y-auto" data-testid="admin-main">
        <div className="p-8 max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
